import { groupBy } from 'lodash';
import { message, Spin } from 'antd';
import { toastConfig } from 'config';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import ReportEditModal from './ReportEditModal';
import { useEffect, useMemo, useState } from 'react';
import controls from 'assets/images/icons/deactivated.svg';
import ComptrollerConfirmModal from '../ComptrollerConfirmModal';
import { activeInactiveStatus, TableStatusMap } from 'pages/globals/global';
import ComptrollerDataTable from '../comptroller-table/ComptrollerDataTableV8';
import IndeterminateCheckbox from 'components/@extended/IndeterminateCheckbox';
import { useReportBulkActiveMutation, useReportBulkDeactivateMutation } from 'store/features/comptroller/comptrollerApi';

const ReportListView = ({ selectedTabName, reportList = [], setReloadTable, reportDataList, isLoading }) => {
	const [value, setValue] = useState(null);
	const [modalData, setModalData] = useState({});
	const [filterData, setFilterData] = useState([]);
	const [showModal, setShowModal] = useState(false);
	const [submitData, setSubmitData] = useState(null);
	const [rowSelection, setRowSelection] = useState({});
	const [selectedRowKeys, setSelectedRowKeys] = useState([]);

	const [type, setType] = useState('View');
	const [editItem, setEditItem] = useState(null);

	const { profileData } = useSelector((state) => state?.profile);

	const [activateReport, { data: activateReportRes }] = useReportBulkActiveMutation();
	const [deactivateReport, { data: deactivateReportRes }] = useReportBulkDeactivateMutation();

	const activeDeactivateHandle = (moduleStatus) => {
		let arr = [];
		if (moduleStatus === 'Active') {
			setValue('deactivated');
			setModalData({
				head: 'Module',
				//action: rowSelection?.length > 1 ? "deactivate all" : "deactivate"
				label:
					rowSelection?.length > 1 ? (
						<p>
							{' '}
							Do you want to <b style={{ fontSize: '1rem' }}>deactivate</b> all?
						</p>
					) : (
						<p>
							Do you want to <b style={{ fontSize: '1rem' }}>deactivate</b> this solution?
						</p>
					),
			});
			if (selectedRowKeys[moduleStatus]) {
				selectedRowKeys[moduleStatus]?.map((item) => {
					arr.push({
						id: item?.id,
						companyCode: profileData?.creatorOrganizationId,
						parent: item?.parent ?? '',
						code: item?.processCode,
						version: item?.version,
						activationStatus: 'Inactive',
					});
					return item;
				});
			}
		} else if (moduleStatus === 'Inactive') {
			setValue('activated');
			setModalData({
				head: 'Module',
				//action: rowSelection?.length > 1 ? "activate all" : "activate"
				label:
					rowSelection?.length > 1 ? (
						<p>
							{' '}
							Do you want to <b style={{ fontSize: '1rem' }}>activate</b> all?
						</p>
					) : (
						<p>
							Do you want to <b style={{ fontSize: '1rem' }}>activate</b> this solution?
						</p>
					),
			});
			if (selectedRowKeys[moduleStatus]) {
				selectedRowKeys[moduleStatus]?.map((item) => {
					arr.push({
						id: item?.id,
					});
					return item;
				});
			}
			if (selectedRowKeys['W-I-P']) {
				selectedRowKeys['W-I-P']?.map((item) => {
					arr.push({
						id: item?.id,
					});
					return item;
				});
			}
		}
		if (arr?.length !== rowSelection?.length) {
			message.error({
				content: 'Your request is inconsistent with selection.',
				icon: <img src={controls} alt="inactive" width="16px" height="16px" style={{ marginRight: '8px', verticalAlign: 'sub' }} />,
			});
		} else {
			setSubmitData(arr);
			setShowModal(true);
		}
	};

	const handleActionSubmit = () => {
		if (submitData) {
			if (value === 'activated') {
				activateReport({ data: submitData });
			} else if (value === 'deactivated') {
				deactivateReport({ data: submitData });
			}
		}
	};

	useEffect(() => {
		if (rowSelection?.length) {
			let arr = [];

			rowSelection?.map((item) => {
				arr.push(item.original);
				return item;
			});

			setSelectedRowKeys(groupBy(arr, 'activationStatus'));
		}
	}, [rowSelection]);

	useEffect(() => {
		let errorCount = 0;
		let successCount = 0;
		let errorMessage = '';
		let successMessage = successCount > 1 ? `Success! ${successCount} rows ${value}` : `Success! ${successCount} row ${value}`;

		activateReportRes?.result?.forEach((item) => {
			if (item?.status === 'SUCCESS') {
				successCount += 1;
			} else if (item?.status === 'FAILED') {
				errorCount += 1;

				errorMessage = item?.message;
			}
		});

		if (successCount) {
			toast.success(successMessage, toastConfig);

			setReloadTable((val) => !val);
			setSubmitData(null);
			setValue(null);
		}

		if (errorCount) {
			toast.error(errorMessage, toastConfig);
		}
	}, [activateReportRes]);

	useEffect(() => {
		let errorCount = 0;
		let successCount = 0;
		let errorMessage = '';
		let successMessage = successCount > 1 ? `Success! ${successCount} rows ${value}` : `Success! ${successCount} row ${value}`;

		deactivateReportRes?.result?.forEach((item) => {
			if (item?.status === 'SUCCESS') {
				successCount += 1;
			} else if (item?.status === 'FAILED') {
				errorCount += 1;

				errorMessage = item?.message;
			}
		});

		if (successCount) {
			toast.success(successMessage, toastConfig);

			setReloadTable((val) => !val);
			setSubmitData(null);
			setValue(null);
		}

		if (errorCount) {
			toast.error(errorMessage, toastConfig);
		}
	}, [deactivateReportRes]);

	const COLUMNS = useMemo(
		() => [
			{
				id: 'Selection',
				header: ({ table }) => (
					<IndeterminateCheckbox
						{...{
							checked: table.getIsAllRowsSelected(),
							indeterminate: table.getIsSomeRowsSelected(),
							onChange: table.getToggleAllRowsSelectedHandler(),
							sx: { color: '#858585', padding: '0px' },
						}}
					/>
				),
				cell: ({ row }) => (
					<IndeterminateCheckbox
						{...{
							checked: row.getIsSelected(),
							disabled: !row.getCanSelect(),
							indeterminate: row.getIsSomeSelected(),
							onChange: row.getToggleSelectedHandler(),
						}}
					/>
				),
			},
			{
				header: 'Report Code',
				key: 'reportCode',
				accessorKey: 'reportCode',
			},
			{
				header: 'Report Name',
				key: 'name',
				accessorKey: 'name',
			},
			// {
			// 	header: 'Module Code',
			// 	key: 'moduleCode',
			// 	accessorKey: 'moduleCode',
			// },
			{
				header: 'Module Name',
				key: 'moduleName',
				accessorKey: 'moduleName',
			},
			// {
			// 	header: 'Sub Module Code',
			// 	key: 'subModuleCode',
			// 	accessorKey: 'subModuleCode',
			// },
			{
				header: 'Sub Module Name',
				key: 'subModuleName',
				accessorKey: 'subModuleName',
			},
			// {
			// 	header: 'Solution Code',
			// 	key: 'processCode',
			// 	accessorKey: 'processCode',
			// },
			// {
			// 	header: 'Solution Version',
			// 	key: 'processVersion',
			// 	accessorKey: 'processVersion',
			// },

			{
				header: 'Created By',
				key: 'createdBy',
				accessorKey: 'createdBy',
			},
			{
				header: 'Status',
				key: 'activationStatus',
				id: 'activationStatus',
				accessorFn: (data) => {
					return TableStatusMap[data.activationStatus];
				},
				cell: (cell) => {
					return activeInactiveStatus(TableStatusMap[cell.row.original.activationStatus]);
				},
				align: 'center',
				cellAlign: 'center',
			},
		],
		[reportDataList]
	);

	return (
		<Spin spinning={isLoading}>
			<ComptrollerDataTable
				COLUMNS={COLUMNS}
				DATA={reportList}
				bulkDelete={false}
				xlsxFileName="report"
				rowSelection={rowSelection}
				setFilterData={setFilterData}
				selectedTabName={selectedTabName}
				setRowSelection={setRowSelection}
				activeDeactivateHandle={activeDeactivateHandle}
				onClickHandler={(row) => {
					setEditItem(row);
				}}
			/>

			{showModal && (
				<ComptrollerConfirmModal showModal={showModal} setShowModal={setShowModal} handleSubmit={handleActionSubmit} modalData={modalData} />
			)}

			{editItem && (
				<ReportEditModal
					type={type}
					setType={setType}
					editItem={editItem}
					dataList={filterData}
					setEditItem={setEditItem}
					setReloadTable={setReloadTable}
					showModal={editItem ? true : false}
				/>
			)}
		</Spin>
	);
};

export default ReportListView;
